import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import useMenu from "hooks/useMenu";
import useScreenSize from "hooks/useScreenSize";
import { useCallback, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { LogoWrap, MenuDesk } from "./styles";

const useStyles = makeStyles((theme: any) => ({
  mainColor: theme.palette.primary.main,
  drop: {
    textDecoration: "none",
  },
  title: {
    textDecoration: "none",
    color: "inherit",
  },
  list: {
    width: "auto",
    [theme.breakpoints.down("mobile")]: {
      width: 250,
    },
  },
  active: {
    background: "rgba(0,0,0,0.2)",
  },
  nav: {
    color: "#fff",
    textDecoration: "none",
    margin: "0 14px",
    padding: "8px 12px",
    borderRadius: "5px",
    [theme.breakpoints.down("mobile")]: {
      color: "#000",
      background: "none",
      width: "214px",
      display: "block",
      textAlign: "center",
      margin: "0",
      borderBottom: "1px solid #ddd",
      position: "relative",
      top: "-6px",
    },
  },
  navHighlight: {
    background: "rgba(255,255,255,0.14)",
    fontWeight: 700,
    border: "1px solid rgba(255,255,255,0.45)",
    transition: "background .2s, transform .2s",
    "&:hover": {
      background: "rgba(255,255,255,0.24)",
    },
    [theme.breakpoints.down("mobile")]: {
      background: "rgba(17,39,88,0.08)",
      border: "none",
      color: "#112758",
      fontWeight: 700,
    },
  },
}));

function Header() {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { screenSize } = useScreenSize({ breakpoint: 1024 });
  const { menuItems, menuItemsMobile } = useMenu();

  const toggleDrawer = useCallback(
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setDrawerOpen(open);
    },
    [],
  );

  const list = (
    <div
      className={classes.list}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        <li key="services">
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive ? `${classes.active} ${classes.nav}` : classes.nav
            }
          >
            Serviços
          </NavLink>
        </li>

        {menuItems.map((item) => (
          <li key={item.name}>
            <NavLink
              to={item.path}
              className={({ isActive }) => {
                const base = item.highlight
                  ? `${classes.nav} ${classes.navHighlight}`
                  : classes.nav;
                return isActive ? `${classes.active} ${base}` : base;
              }}
            >
              {item.text}
            </NavLink>
          </li>
        ))}
      </List>
    </div>
  );

  const listMobile = (
    <div
      className={classes.list}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {menuItemsMobile.map((item) => (
          <li key={item.name}>
            <NavLink
              to={item.path}
              className={({ isActive }) => {
                const base = item.highlight
                  ? `${classes.nav} ${classes.navHighlight}`
                  : classes.nav;
                return isActive ? `${classes.active} ${base}` : base;
              }}
            >
              {item.text}
            </NavLink>
          </li>
        ))}
      </List>
    </div>
  );

  return (
    <header>
      <AppBar position="static">
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="h6"
            className={classes.title}
            component={Link}
            to="/"
            aria-label="Ir para a página inicial"
            data-testid="logo-link"
          >
            <LogoWrap>
              <img
                width={24}
                height={24}
                src="/assets/logo-sistemas-flex-header.webp"
                alt="Logotipo da Sistemas Flex representado por uma engrenagem"
              />
              <h1
                style={{
                  fontSize: "inherit",
                  lineHeight: "1",
                  fontFamily: "inherit",
                  fontWeight: 500,
                }}
              >
                Sistemas Flex
              </h1>
            </LogoWrap>
          </Typography>
          {screenSize.width > 1024 ? (
            <MenuDesk>{list}</MenuDesk>
          ) : (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              aria-haspopup="true"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        className="menu-mob"
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        role="presentation"
        aria-label="menu mobile"
      >
        {listMobile}
      </Drawer>
    </header>
  );
}

export default Header;
